/**
 * Drag handle for a side panel's width.
 *
 * Sits on the panel's inner edge as an 8px hit strip carrying a blue grip pill at
 * mid-height — the same pill the artboard's resize grips use, so both read as
 * "drag this edge". The grip is ALWAYS visible: a strip that only lit up on hover
 * was undiscoverable, nothing said the panel could be widened at all. Hover and
 * drag take it to full opacity and add a full-height rule.
 *
 * Pointer capture keeps the drag alive when the cursor outruns the strip, and the
 * width is remembered per storage key across sessions.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** Read a persisted width, clamped — falls back to `fallback` when unset/bad. */
export function loadPanelWidth(storageKey, fallback, min, max) {
    if (typeof window === 'undefined') return fallback;
    const raw = Number(window.localStorage?.getItem(storageKey));
    if (!Number.isFinite(raw) || raw <= 0) return fallback;
    return Math.min(max, Math.max(min, raw));
}

export default function PanelResizer({
    width,
    onWidth,
    min = 240,
    max = 640,
    // Width double-click restores. Defaults to `min` for callers that don't have a
    // separate default width.
    resetTo,
    // 'left' — the handle is on the panel's left edge and dragging left widens it
    // (a right-hand panel). 'right' is the mirror, for a left-hand panel.
    edge = 'left',
    storageKey,
}) {
    const [dragging, setDragging] = useState(false);
    const startRef = useRef({ x: 0, w: 0 });

    const onPointerDown = useCallback((e) => {
        e.preventDefault();
        // Capture is an optimisation, not a requirement — the window-level move
        // listener below carries the drag either way, so a browser that refuses
        // it must not take the whole gesture down with it.
        try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* not captureable */ }
        startRef.current = { x: e.clientX, w: width };
        setDragging(true);
    }, [width]);

    const onPointerMove = useCallback((e) => {
        if (!dragging) return;
        const delta = e.clientX - startRef.current.x;
        const next = Math.min(max, Math.max(min, startRef.current.w + (edge === 'left' ? -delta : delta)));
        onWidth(next);
    }, [dragging, edge, max, min, onWidth]);

    const endDrag = useCallback(() => {
        if (!dragging) return;
        setDragging(false);
        if (storageKey && typeof window !== 'undefined') {
            try { window.localStorage.setItem(storageKey, String(width)); } catch { /* private mode */ }
        }
    }, [dragging, storageKey, width]);

    // A drag that leaves the window still has to end, or the panel keeps tracking
    // the pointer after the button is already up. Window-level move keeps the drag
    // going when the pointer outruns the strip and capture didn't take.
    useEffect(() => {
        if (!dragging) return undefined;
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', endDrag);
        window.addEventListener('pointercancel', endDrag);
        // Kill text selection for the length of the drag — dragging a divider
        // across the canvas otherwise highlights everything it passes over.
        const prevSelect = document.body.style.userSelect;
        const prevCursor = document.body.style.cursor;
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';
        return () => {
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', endDrag);
            window.removeEventListener('pointercancel', endDrag);
            document.body.style.userSelect = prevSelect;
            document.body.style.cursor = prevCursor;
        };
    }, [dragging, endDrag, onPointerMove]);

    // Double-click restores the default, the usual escape hatch from a bad drag.
    const reset = () => {
        const target = Math.min(max, Math.max(min, Number(resetTo) || min));
        onWidth(target);
        if (storageKey && typeof window !== 'undefined') {
            try { window.localStorage.setItem(storageKey, String(target)); } catch { /* private mode */ }
        }
    };

    return (
        <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize panel"
            title="Drag to resize this panel · double-click to reset"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onDoubleClick={reset}
            className={`absolute top-0 bottom-0 ${edge === 'left' ? '-left-1' : '-right-1'}
                w-2 z-20 cursor-col-resize group select-none touch-none`}
        >
            {/* Full-height rule — the "you are dragging this edge" feedback */}
            <span
                className={`absolute inset-y-0 left-1/2 -translate-x-1/2 w-px transition-colors
                    ${dragging ? 'bg-[#0d99ff]' : 'bg-transparent group-hover:bg-[#0d99ff]'}`}
            />
            {/* Grip — the resting affordance, deliberately the SAME blue pill the
                artboard's own resize grips use in CanvasViewport (6×28, half opacity
                until you reach for it), so "this edge is draggable" reads the same
                way on the canvas and on the panels. */}
            <span
                aria-hidden="true"
                className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                    h-7 w-[6px] rounded-full bg-[#0d99ff] transition-opacity
                    ${dragging ? 'opacity-100' : 'opacity-50 group-hover:opacity-100'}`}
            />
        </div>
    );
}
